import { expectTypeOf } from 'expect-type';
import ko from 'knockout';
import type moment from 'moment';

import './extenders';

function _observableNumber<T extends number>(value: T, precision?: number, dfault?: T): ko.Observable<T> {
    return ko.observable<T>(value).extend({ number: { precision, default: dfault } });
}
ko.observableNumber = _observableNumber;
declare module 'knockout' {
    function observableNumber<T extends number>(value: T, precision?: number, dfault?: T): Observable<T>;
}

function _observableDate(
    value?: moment.Moment,
    dateonly?: boolean,
    dfault?: moment.Moment,
): ko.Observable<moment.Moment | undefined> {
    return ko.observable<moment.Moment | undefined>(value).extend({ date: { dateonly, default: dfault } });
}
ko.observableDate = _observableDate;
declare module 'knockout' {
    function observableDate(
        value?: moment.Moment,
        dateonly?: boolean,
        dfault?: moment.Moment,
    ): Observable<moment.Moment | undefined>;
}

/**
 * An observable which acts as a computed until it is overwritten and then acts as an observable.
 * @param fn - The function to sync with.
 */
function _overwritableComputed<T>(fn: () => T): ko.OverwritableComputed<T> {
    const overwritten = ko.observable(false);
    const overwrite = ko.observable();
    const ret = ko.pureComputed<T>({
        read: () => (overwritten() ? overwrite() : fn()),
        write: val => {
            overwrite(val);
            overwritten(true);
        },
    });
    const sync = (val: () => T) => {
        fn = val;
        overwritten(false);
    };
    return Object.assign(ret, { sync });
}
ko.overwritableComputed = _overwritableComputed;
declare module 'knockout' {
    function overwritableComputed<T>(fn: () => T): OverwritableComputed<T>;
    interface OverwritableComputed<T> extends PureComputed<T> {
        sync: (val: () => T) => void;
    }
}

/**
 * A computed for async functions.
 *
 * Ideally an async computed would simply take an async function and do its thing in the same way as with a normal sync
 * computed. The problem is that knockout's dependency tracking only works synchronously - if used on an async function,
 * everything after the first await will not be eligible for tracking.
 * It is possible to write the async function in such a way that all dependencies are established before the first
 * await, but expecting developers to constantly be aware of this nuance will be error-prone.
 * For this reason, we split the functionality into two functions - the first is a sync function that explicitly
 * establishes dependencies, and the second is an async function that performs the async operations using the values
 * returned by the first function.
 *
 * In the past there existed a common pattern where a subscription would be manually created on some values that would
 * execute an async function and upon completion set the value in another observable. The problem with this approach is
 * that it hides the async nature of the operation. Once the subscribed values change, the observable is potentially out
 * of sync until its new value arrives. An alternative approach is to clear the observable immediately and repopulate it
 * once the new value is available. This is slightly better since the value is empty rather than invalid, but callers
 * still have no way of knowing whether an empty value meant really empty or just that an update was in progress.
 * The underlying issue was that an async problem was treated as a sync problem. To eliminate these issues entirely we
 * make the computed always return a promise. This forces callers to treat it asynchronously.
 *
 * In the case where the dependencies reevaluate while the computed is already pending, the promise is not recreated.
 * Instead, the existing promise waits until the last (current) result is available, skipping any intermediate results.
 * Despite this being the correct approach, an unfortunate side-effect is that intermediate objects returned by the
 * async function are never seen by the calling code and may leak (due to never being composed or otherwise cleaned).
 * In addition, since there is no way to cancel a Promise, any outdated executors will continue doing unnecessary work
 * until they complete.
 * To solve both these problems, the executor receives an `isCurrent` property in its second argument which it can call
 * between async steps to check if it is worth continuing.
 *
 * @param subscriber - The function that establishes the computed's dependencies. This is the function you would
 *   normally pass to the standard pure computed.
 * @param executor - The async function which returns the new value of the computed. Before this function is called, the
 *   value of the computed is set to undefined, and once the returned promise resolves, its value becomes the new value
 *   of the computed.
 */
function _asyncComputed<T, D>(subscriber: () => D, executor: (data: D, meta: AsyncComputedMeta) => Promise<T>) {
    // The current promise and its resolve/reject callbacks.
    // If another async operation is started before the previous one has ended, the same promise is reused.
    // It is created empty, but will always have a value once the async computed is activated.
    const promise = ko.observable<Promise<T>>(undefined as unknown as Promise<T>);
    let resolve: ((val: T) => void) | undefined;
    let reject: ((err: unknown) => void) | undefined;

    const pending = ko.observable<boolean>(false);
    const value = ko.observable<T | undefined>();
    const error = ko.observable<unknown>();

    // Incremented each time the data returned by the subscriber changes.
    const version = ko.observable(0);

    const _subscriber = ko
        .pureComputed(() => {
            ko.ignoreDependencies(() => version(version() + 1));
            version(); // Establish dependency.
            return subscriber();
        })
        .extend({ notify: 'always', debounce: 1 });

    /**
     * Call the executor to refresh the results.
     * @param cur_version - The current result version.
     * @param data - The data to call the executor with.
     */
    async function execute(cur_version: number, data: D) {
        // Only recreate the promise if the previous promise was resolved. If it was still pending, keep using it.
        if (!pending()) {
            promise(
                new Promise((_resolve, _reject) => {
                    resolve = _resolve;
                    reject = _reject;
                }),
            );
        }

        // Reset states.
        pending(true);
        value(undefined);
        error(undefined);

        // Perform the async operation. If this request is still the current one by the time it completes, set the
        // value/error, resolve/reject the promise accordingly and set pending to false.
        const isCurrent = () => cur_version === version();
        try {
            const val = await executor(data, { isCurrent });
            if (isCurrent()) {
                resolve?.(val);
                value(val);
                pending(false);
            }
        } catch (e) {
            if (isCurrent()) {
                reject?.(e);
                error(e);
                pending(false);
            }
        }
    }

    // The actual pure computed that is returned. This establishes dependencies on the subscriber as well as the promise
    // once it completes. We only execute if the data changes, so the source of the reevaluation must be distinguished.
    let fetched_version = 0;
    const result = ko
        .pureComputed(() => {
            const data = _subscriber();
            if (fetched_version !== version.peek()) {
                fetched_version = version.peek();
                ko.ignoreDependencies(() => execute(version.peek(), data));
            }

            return promise();
        })
        .extend({ equality: '===' });

    return Object.assign(result, {
        pending: () => pending(),
        value: () => {
            result(); // Establish dependencies.
            return value();
        },
        lazyValue: () => value(),
        error: () => {
            result(); // Establish dependencies.
            return error();
        },
        refresh: () => {
            version(version() + 1);
        },
        [IS_ASYNC_COMPUTED]: true,
    });
}
function _isAsyncComputed(val: unknown): val is ko.AsyncComputed<unknown> {
    return !!(val as { [IS_ASYNC_COMPUTED]?: boolean })?.[IS_ASYNC_COMPUTED];
}
const IS_ASYNC_COMPUTED = Symbol('isAsyncComputed');

ko.asyncComputed = _asyncComputed;
ko.isAsyncComputed = _isAsyncComputed;
declare module 'knockout' {
    function asyncComputed<T, D>(
        subscriber: () => D,
        executor: (data: D, meta: AsyncComputedMeta) => Promise<T>,
    ): AsyncComputed<T>;
    // eslint-disable-next-line typescript/no-explicit-any
    interface AsyncComputed<T = any> extends PureComputed<Promise<T>> {
        /** Whether or not the promise is currently in the pending state. */
        pending: () => boolean;
        /**
         * Contains the value of the promise if it is resolved, and undefined if it is pending or rejected.
         * This is useful in cases where you need to react to changes in the computed's eventual value without caring
         * about intermediate pending states.
         */
        value: () => T | undefined;
        /**
         * Like value, but won't establish dependencies on the subscriber and won't call the executor. It only returns
         * the current value that was set as a result of other non-lazy calls.
         */
        lazyValue: () => T | undefined;
        /**
         * Manually refresh the async computed, causing the executor to run again.
         * Use this if you know the result of the executor will change, even though the subscriber has not.
         */
        refresh: () => void;
        /** Like value, but contains the error if the promise rejected. */
        error: () => unknown;
    }

    function isAsyncComputed(val: unknown): val is AsyncComputed<unknown>;
}
type AsyncComputedMeta = { isCurrent: () => boolean };

// @ts-expect-error Unused, only for tests.
function _Tests() {
    const obs = ko.observable('string');
    ko.asyncComputed(obs, async x => {
        expectTypeOf(x).toEqualTypeOf<string>();
        return x;
    });
}
